import { useState } from "react";
import { API_CONFIG } from "../config/api";

/**
 * Generic list + CRUD controller for any service whose `getAll` returns
 * `{ data: { list, total } }`. Used by every admin list screen.
 *
 *   const { rows, pagination, loading, fetchRows, ... } =
 *     useListController(familiesService);
 */
export const useListController = (service) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({});
  const [pagination, setPagination] = useState({
    page: 1,
    limit: API_CONFIG.PAGINATION.DEFAULT_LIMIT,
    total: 0,
  });

  const fetchRows = async (params = {}) => {
    setLoading(true);
    setError("");
    try {
      const body = {
        page: params.page ?? pagination.page,
        limit: params.limit ?? pagination.limit,
        search: params.search ?? filters.search ?? "",
        status: params.status ?? filters.status ?? "",
        ...filters,
        ...params,
      };
      const res = await service.getAll(body);
      const list = res?.data?.list || (Array.isArray(res?.data) ? res.data : []);
      const total = res?.data?.total ?? list.length;
      setRows(list);
      setFilters((f) => ({ ...f, search: body.search, status: body.status }));
      setPagination((prev) => ({
        ...prev,
        page: body.page,
        limit: body.limit,
        total,
      }));
      return { success: true, data: { list, total } };
    } catch (err) {
      const message = err.response?.data?.msg || err.message;
      setError(message);
      setRows([]);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  };

  const wrap = (fn) => async (...args) => {
    try {
      const res = await fn(...args);
      if (!res?.success) return { success: false, error: res?.msg };
      return { success: true, data: res.data };
    } catch (err) {
      return { success: false, error: err.response?.data?.msg || err.message };
    }
  };

  const createRow = wrap((data) => service.create(data));
  const updateRow = wrap((data) => service.update(data));
  const updateStatus = wrap((id, is_active) => service.updateStatus(id, is_active));
  const deleteRow = wrap((id) => service.delete(id));

  return {
    loading,
    error,
    rows,
    pagination,
    filters,
    setFilters,
    fetchRows,
    createRow,
    updateRow,
    updateStatus,
    deleteRow,
    clearError: () => setError(""),
  };
};

export default useListController;
